import { useState } from 'react';
import Link from 'next/link';
import ImageRefuse from '@/assets/img/image-refuse.svg';
import ModalDefault from '@/components/Modals/ModalDefault';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ROUTER_PAGES } from '@/utils/constants';
import { MyErrorResponse } from '@/utils/handlers';
import {
  Box,
  FormControlLabel,
  Typography,
  Checkbox,
  TextField,
} from '@mui/material';
import { AxiosError } from 'axios';

enum DeactivateAccountModalViews {
  AGREE_TERMS,
  INFORM_REASON,
}

interface IDeleteRequests {
  id: number;
  observations: string;
  is_pending: boolean;
  is_canceled: boolean;
  created_at: string;
  canceled_at: string | null;
  deleted_at: string | null;
}

interface IDeactivateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (value: IDeleteRequests) => void;
}

interface IErrorObservations {
  isError?: boolean;
  textError?: string;
}

export const DeactivateAccountModal = ({
  isOpen,
  onClose,
  onSuccess,
}: IDeactivateAccountModalProps) => {
  const { setIsLoading } = useLoading();
  const [currentView, setCurrentView] = useState(
    DeactivateAccountModalViews.AGREE_TERMS
  );
  const [isCheckedTerms, setIsCheckedTerms] = useState(false);
  const [observations, setObservations] = useState('');
  const [errorObservation, setErrorObservation] =
    useState<IErrorObservations>();
  const handleClickConfirmDeactivate = () => {
    if (!isCheckedTerms)
      return Toast(
        'error',
        'Para prosseguir é necessário concordar com os termos de uso e a política de privacidade'
      );

    setCurrentView(prevState => prevState + 1);
  };

  const handleClickDeactivateAccount = async () => {
    setIsLoading(true);

    try {
      if (!observations) {
        setErrorObservation({
          isError: true,
          textError: 'Este campo não pode ser em branco.',
        });
        return;
      }
      if (errorObservation?.isError) return;
      const { status, data } = await api.post<IDeleteRequests>(
        '/auth/users/delete_account/',
        {
          observations,
        }
      );

      if (status === 201) {
        Toast(
          'success',
          'Que pena! Sua conta será desativada após 30 dias, até lá poderá usá-la normalmente.'
        );
        onSuccess(data);
        onClose();
      }
    } catch (err) {
      const errorData = (err as AxiosError<MyErrorResponse>)?.response?.data;
      if (errorData?.observations) {
        Toast('error', errorData.observations[0]);
      } else {
        Toast(
          'error',
          errorData?.detail || 'Houve um erro inesperado, tente novamente!'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentModalDefaultProps = {
    [DeactivateAccountModalViews.AGREE_TERMS]: {
      title: 'Deseja desativar a conta?',
      subtitle:
        'Sua conta ficará inativa e após 30 dias será excluída permanentemente',
      textButton: 'Confirmar',
      onClickButton: handleClickConfirmDeactivate,
    },
    [DeactivateAccountModalViews.INFORM_REASON]: {
      title: 'Qual o motivo da desativação?',
      textButton: 'Enviar',
      onClickButton: handleClickDeactivateAccount,
    },
  }[currentView];

  return (
    <ModalDefault
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setCurrentView(DeactivateAccountModalViews.AGREE_TERMS);
        setIsCheckedTerms(false);
      }}
      src={
        DeactivateAccountModalViews.AGREE_TERMS === currentView && ImageRefuse
      }
      alt="Imagem de ilustração para representa a desativação de conta"
      {...currentModalDefaultProps}
    >
      <>
        {
          {
            [DeactivateAccountModalViews.AGREE_TERMS]: (
              <Box
                sx={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  mt: 4,
                  flexWrap: 'wrap',
                  [theme.breakpoints.down(470)]: {
                    mt: 3,
                  },
                })}
              >
                <FormControlLabel
                  sx={{
                    mr: 0,
                    cursor: 'default',
                  }}
                  control={
                    <Checkbox
                      onChange={() =>
                        setIsCheckedTerms(prevState => !prevState)
                      }
                      checked={isCheckedTerms}
                      sx={{ p: 0, ml: 1, mr: 0.5 }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ mr: 0.5 }}>
                      Li e concordo com os
                    </Typography>
                  }
                />
                <Link href={ROUTER_PAGES.termosUso} passHref>
                  <a target="_blank" rel="noreferrer">
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer' }}
                      color="primary"
                    >
                      termos de uso
                    </Typography>
                  </a>
                </Link>
                <Typography variant="body2">e</Typography>
                <Link href={ROUTER_PAGES.privacidade} passHref>
                  <a target="_blank" rel="noreferrer">
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer' }}
                      color="primary"
                    >
                      política de privacidade
                    </Typography>
                  </a>
                </Link>
              </Box>
            ),
            [DeactivateAccountModalViews.INFORM_REASON]: (
              <Box
                sx={{
                  my: 3,
                }}
              >
                <TextField
                  required
                  variant="standard"
                  placeholder="Entrei no app só de curiosidade."
                  fullWidth
                  label="Motivo"
                  value={observations}
                  error={errorObservation?.isError}
                  helperText={
                    errorObservation?.isError && errorObservation?.textError
                  }
                  onChange={e => {
                    setErrorObservation({
                      isError: false,
                    });
                    setObservations(e.target.value);
                  }}
                />
              </Box>
            ),
          }[currentView]
        }
      </>
    </ModalDefault>
  );
};

export type { IDeactivateAccountModalProps, IDeleteRequests };
